/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {DownloadJob, DownloadJobTask, GenericObject} from '@ion/core/src/types/entities';
import {JobProgressUpdate, JobTaskProgressPatch} from '@ion/services/src/websocket/types';

export type JobCacheTask = DownloadJobTask & {
  updatedOn?: string,
  taskIndex?: number
};

export type JobCacheItem = Omit<DownloadJob, 'tasks' | 'task' | 'progressStatus'> & {
  jobId?: number,
  tasks?: JobCacheTask[],
  taskCount?: number,
  username?: string,
  updatedOn?: string,
  lastModifiedOn?: string,
  progressStatus?: string
};

export interface JobProgressUpdateOptions {
  allowUnknownTasks?: boolean
}

export interface JobCacheMergeResult {
  accepted: boolean,
  job?: JobCacheItem,
  unplaced?: number[]
}

export interface JobReplayResult {
  job: JobCacheItem,
  moved: boolean
}

type ModifiedTimeSource = {lastModifiedOn?: string, updatedOn?: string};

export const toModifiedTime = (entity?: ModifiedTimeSource): number => (
  Date.parse(entity?.lastModifiedOn ?? entity?.updatedOn ?? '')
);

export const toTaskPatch = (
  {taskIndex, version, lastModifiedOn, progress, counts}: JobTaskProgressPatch
): JobCacheTask => ({
  taskIndex,
  version,
  lastModifiedOn,
  progress,
  counts
});

const isAtLeastAsNew = (candidate: number, held: number): boolean => (
  !isNaN(candidate) && !(held > candidate)
);

const defined = <T extends GenericObject>(values: T): Partial<T> => {
  const held: GenericObject = {};

  Object.keys(values).forEach(key => {
    if (values[key] !== undefined) {
      held[key] = values[key];
    }
  });
  return held as Partial<T>;
};

export const mergeIntoJob = (
  cached: JobCacheItem | undefined,
  delta: JobProgressUpdate,
  options?: JobProgressUpdateOptions
): JobCacheMergeResult => {
  let result: JobCacheMergeResult = {accepted: false};

  if (cached) {
    const merged             = {...cached};
    const tasks              = [...(cached.tasks ?? [])];
    const unplaced: number[] = [];
    let accepted             = false;

    if (isAtLeastAsNew(toModifiedTime(delta), toModifiedTime(cached))) {
      accepted = true;

      Object.assign(merged, defined({
        progress       : delta.progress,
        progressStatus : delta.progress?.status,
        version        : delta.version,
        lastModifiedOn : delta.lastModifiedOn
      }));
    }
    (delta.tasks ?? []).forEach(change => {
      const position = tasks.findIndex(task => task.taskIndex === change.taskIndex);
      const current  = position === -1 ? undefined : tasks[position];

      if (position !== -1 || options?.allowUnknownTasks) {
        if (isAtLeastAsNew(toModifiedTime(change), toModifiedTime(current))) {
          const patched = {
            ...current,
            ...defined({
              ...toTaskPatch(change),
              progressStatus : change.progress?.status
            })
          };
          accepted = true;
          if (position === -1) {
            tasks.push(patched);
          } else {
            tasks[position] = patched;
          }
        }
      } else {
        unplaced.push(change.taskIndex);
      }
    });
    if (accepted) {
      merged.tasks = tasks;
    }
    result = {
      accepted,
      job : accepted ? merged : cached,
      unplaced
    };
  }

  return result;
};

export const mergeSnapshot = (
  cached: JobCacheItem | undefined, incoming: JobCacheItem
): JobCacheItem => {
  let merged: JobCacheItem = incoming;

  if (cached) {
    merged = {...incoming};

    if (isAtLeastAsNew(toModifiedTime(cached), toModifiedTime(incoming))) {
      Object.assign(merged, defined({
        progress       : cached.progress,
        progressStatus : cached.progressStatus,
        version        : cached.version,
        lastModifiedOn : cached.lastModifiedOn
      }));
    }
    merged.tasks = incoming.tasks?.map(task => {
      const current = cached.tasks?.find(cachedTask => cachedTask.taskIndex === task.taskIndex);
      let patched   = task;

      if (isAtLeastAsNew(toModifiedTime(current), toModifiedTime(task))) {
        patched = {
          ...task,
          ...defined({
            progress       : current?.progress,
            progressStatus : current?.progressStatus,
            counts         : current?.counts,
            version        : current?.version,
            lastModifiedOn : current?.lastModifiedOn
          })
        };
      }

      return patched;
    });
  }

  return merged;
};

export const hasJobChanges = (merged: JobCacheItem, incoming: JobCacheItem): boolean => (
  merged.progress !== incoming.progress ||
  merged.progressStatus !== incoming.progressStatus ||
  merged.version !== incoming.version ||
  merged.lastModifiedOn !== incoming.lastModifiedOn ||
  !!merged.tasks?.some((task, index) => task !== incoming.tasks?.[index])
);

/**
 * Reconciles one loaded item against the entry it replaces, then replays the
 * changes which arrived while the request was in flight.
 *
 * This is the whole of what a settling request has to do to one job, and both
 * live-update surfaces do it identically - the grid per item of a loaded block,
 * the job tab for the single job it renders - so it lives here rather than
 * twice in the hooks.
 *
 * Two passes, and the order is not interchangeable. The first is a snapshot
 * comparison against what the entry held before the request went out; the
 * second is the same guard a live change is put through, so a replay which is
 * no longer the newest for its scope is rejected exactly as a late change
 * would be. Replaying before reconciling would judge each change against the
 * pre-request item and let a superseded one win.
 *
 * `moved` reports whether the caller must write and repaint: an item nothing
 * moved is already stored and already painted from the response.
 *
 * @param   cached The item as the entry held it *before* awaiting the
 *          response, or `undefined` when the entry held none - in which case
 *          the response is the whole story and nothing moved.
 * @param   incoming The item the response brought.
 * @param   changes The buffered changes for this job, in arrival order.
 * @returns `job`, the reconciled item, and `moved`, `true` when it differs
 *          from what the response brought.
 */
export const reconcileWithReplay = (
  cached: JobCacheItem | undefined,
  incoming: JobCacheItem,
  changes?: JobProgressUpdate[]
): JobReplayResult => {
  let job   = cached ? mergeSnapshot(cached, incoming) : incoming;
  let moved = !!cached && hasJobChanges(job, incoming);

  changes?.forEach(change => {
    const {accepted, job: replayed} = mergeIntoJob(job, change);

    if (accepted && replayed) {
      job   = replayed;
      moved = true;
    }
  });

  return {job, moved};
};
